/**
 * Shared display helpers for recent history items (sidebar widget + "all recents" sheet):
 * model icon resolution, translated type labels, status dot colors and relative times.
 */

import { usePage } from "@inertiajs/vue3";
import { FileText } from "@lucide/vue";
import { formatDistanceToNowStrict } from "date-fns";
import { computed, type Component } from "vue";
import { useI18n } from "vue-i18n";

import { useDateFormat } from "@ja/core-ui/lib/date";
import { resolveLucideIcon } from "@ja/portal-auth/utils/lucideIcon";

import type { RecentHistoryItem, SharedCrudData } from "../types";

/** Status dot color keys accepted from the server config. */
const STATUS_DOT_CLASSES: Record<string, string> = {
    amber: "bg-amber-500",
    orange: "bg-amber-500",
    blue: "bg-sky-500",
    green: "bg-emerald-500",
    red: "bg-red-500",
    gray: "bg-muted-foreground"
};

interface UseRecentItemDisplayReturn {
    /** Resolve the lucide icon component for an item's model */
    modelIcon: (item: RecentHistoryItem) => Component;
    /** Translated singular type label for an item (e.g. "Facture") */
    typeLabel: (item: RecentHistoryItem) => string;
    /** Tailwind class for the status dot color */
    statusDotClass: (item: RecentHistoryItem) => string;
    /** Suffixed relative time (e.g. "il y a 7 jours") */
    relativeTime: (dateString: string) => string;
}

export function useRecentItemDisplay(): UseRecentItemDisplayReturn {
    const page = usePage<{ crud?: SharedCrudData }>();
    const { t: translate, te: translationExists } = useI18n();
    const { locale } = useDateFormat();

    const modelIcons = computed(() => {
        const icons: Record<string, string | undefined> = {};
        for (const model of page.props.crud?.models ?? []) {
            icons[model.key] = model.icon;
        }
        return icons;
    });

    function modelIcon(item: RecentHistoryItem): Component {
        return resolveLucideIcon(modelIcons.value[item.model], FileText);
    }

    function typeLabel(item: RecentHistoryItem): string {
        if (item.translationPrefix) {
            const key = `models.${item.translationPrefix}.singular`;
            if (translationExists(key)) return translate(key);
        }
        return item.model.replace(/[-_]/g, " ");
    }

    function statusDotClass(item: RecentHistoryItem): string {
        return (
            STATUS_DOT_CLASSES[item.status_color ?? ""] ??
            STATUS_DOT_CLASSES.amber
        );
    }

    function relativeTime(dateString: string): string {
        if (!dateString) return "";
        return formatDistanceToNowStrict(new Date(dateString), {
            addSuffix: true,
            locale: locale.value
        });
    }

    return { modelIcon, typeLabel, statusDotClass, relativeTime };
}
